import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Download, Mail, Phone, MapPin, Building2, TrendingUp, Award } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ProviderProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const provider = {
    id: id,
    name: 'Dr. Gerard Nikodem',
    phone: '(636) 937-9133',
    email: 'gcniko@aol.com',
    practice: 'Gerard Nikodem',
    address: '1121 West Gannon Drive, Festus, Missouri, 63028',
    specialty: 'General Dentist',
    referralCount: 237,
    birthday: 'May 4th',
    relationshipScore: 92,
    avgResponseTime: '2.3 hours',
    satisfactionRate: 98,
    claimed: false
  };

  const scoreHistory = [
    { month: 'Dec', score: 78, referrals: 18 },
    { month: 'Jan', score: 82, referrals: 22 },
    { month: 'Feb', score: 85, referrals: 24 },
    { month: 'Mar', score: 87, referrals: 21 },
    { month: 'Apr', score: 88, referrals: 25 },
    { month: 'May', score: 89, referrals: 23 },
    { month: 'Jun', score: 90, referrals: 26 },
    { month: 'Jul', score: 91, referrals: 24 },
    { month: 'Aug', score: 91, referrals: 22 },
    { month: 'Sep', score: 92, referrals: 20 },
    { month: 'Oct', score: 92, referrals: 19 },
    { month: 'Nov', score: 92, referrals: 13 }
  ];

  const exportToCSV = () => {
    let csvContent = 'Month,Relationship Score,Referrals\n';
    scoreHistory.forEach(row => {
      csvContent += `${row.month},${row.score},${row.referrals}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `provider_${provider.name.replace(/\s+/g, '_')}_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    alert('PDF export functionality - would generate detailed provider report with charts and statistics');
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <Button variant="ghost" onClick={() => navigate('/doctors')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Directory
        </Button>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={exportToCSV}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button variant="outline" onClick={exportToPDF}>
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
          <Button 
            className="bg-[#D4AF37] text-[#0A1628] hover:bg-[#C19B2B]"
            onClick={() => navigate(`/doctors/${id}/edit`)}
          >
            Edit Provider
          </Button>
        </div>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-3">
                <h1 className="text-3xl font-bold text-[#0A1628]">{provider.name}</h1>
                <Badge className={provider.claimed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                  {provider.claimed ? 'Verified' : 'Unclaimed'}
                </Badge>
              </div>
              <p className="text-lg text-gray-700 mb-2">{provider.practice}</p>
              <p className="text-sm text-gray-600 mb-4">{provider.specialty}</p>
              
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  {provider.phone}
                </div>
                <div className="flex items-center text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  {provider.email}
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin className="h-4 w-4 mr-2" />
                  {provider.address}
                </div>
              </div>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-[#0A1628] to-[#1a2332] rounded-lg text-white">
              <div className="text-4xl font-bold text-[#D4AF37] mb-1">{provider.relationshipScore}</div>
              <div className="text-sm opacity-90">Relationship Score</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Relationship Score History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={scoreHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#888" />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }} />
                  <Legend />
                  <Line type="monotone" dataKey="score" stroke="#D4AF37" strokeWidth={3} dot={{ fill: '#D4AF37', r: 5 }} name="Relationship Score" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Award className="h-5 w-5 mr-2 text-[#D4AF37]" />
                Monthly Referral Volume
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={scoreHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#888" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px' }} />
                  <Legend />
                  <Line type="monotone" dataKey="referrals" stroke="#0A1628" strokeWidth={2} dot={{ fill: '#0A1628', r: 4 }} name="Referrals" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Performance Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Referrals</p>
                <p className="text-2xl font-bold text-[#0A1628]">{provider.referralCount}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Avg Response Time</p>
                <p className="text-xl font-bold text-[#D4AF37]">{provider.avgResponseTime}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Satisfaction Rate</p>
                <p className="text-xl font-bold text-green-600">{provider.satisfactionRate}%</p>
              </div>
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-600 mb-1">Last Referral</p>
                <p className="font-semibold text-[#0A1628]">{provider.lastReferral}</p>
              </div>
              {provider.birthday && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Birthday</p>
                  <p className="font-semibold text-[#D4AF37]">{provider.birthday}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button className="w-full" variant="outline">
                <Mail className="h-4 w-4 mr-2" />
                Send Message
              </Button>
              <Button className="w-full" variant="outline">
                Create Referral
              </Button>
              <Button className="w-full" variant="outline">
                View All Referrals
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProviderProfile;
